import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles, ShieldCheck, Clock, Info } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { SECTEURS } from "../data/projects";
import { supabase, isSupabaseReady } from "../lib/supabaseClient";
import Seo from "../components/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const secteurOptions = SECTEURS.filter((s) => s.id !== "tous");

const bullets = [
  {
    icon: Sparkles,
    title: "Un aperçu réel, pas un template",
    text: "Une maquette pensée pour VOTRE commerce : vos couleurs, votre ambiance, votre univers.",
  },
  {
    icon: Clock,
    title: "Livré en 3 à 5 jours",
    text: "On analyse votre univers et on vous envoie votre aperçu directement, sans attendre.",
  },
  {
    icon: ShieldCheck,
    title: "Aucune carte bancaire. Aucun engagement",
    text: "Vous découvrez le rendu, et vous décidez ensuite. Zéro pression.",
  },
];

export default function MockupGratuit() {
  const [form, setForm] = useState({
    commerce: "",
    secteur: "",
    reseau: "",
    envie: "",
    email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.commerce.trim() || !form.secteur) {
      toast.error("Indiquez au moins le nom de votre commerce et votre secteur.");
      return;
    }
    if (!form.email.trim() && !form.reseau.trim()) {
      toast.error("Laissez au moins un moyen de vous joindre : Instagram/site ou email.");
      return;
    }

    const secteurLabel =
      secteurOptions.find((s) => s.id === form.secteur)?.label || form.secteur;

    const message =
      `Demande d'aperçu gratuit (mockup)\n` +
      `— Commerce : ${form.commerce}\n` +
      `— Secteur : ${secteurLabel}\n` +
      `— Instagram / site : ${form.reseau.trim() || "—"}\n` +
      `— Souhaite améliorer : ${form.envie.trim() || "—"}\n` +
      `— Email fourni : ${form.email.trim() || "non (à recontacter en DM)"}`;

    // L'API exige un email valide : si le prospect n'en laisse pas,
    // on route le lead vers l'adresse ML (le contact se fera via Instagram).
    const emailToSend = form.email.trim() || "ml.agence04@gmail.com";

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.commerce,
        email: emailToSend,
        phone: "",
        project_type: `Aperçu gratuit — ${secteurLabel}`,
        message,
      });
      // Enregistrement du lead dans Supabase (best-effort, ne bloque pas)
      if (isSupabaseReady) {
        supabase.from("leads").insert({
          source: "mockup",
          email: form.email.trim() || null,
          commerce: form.commerce.trim(),
          secteur: secteurLabel,
          payload: { reseau: form.reseau.trim() || null, envie: form.envie.trim() || null },
        }).then(() => {}, () => {});
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Réessayez ou écrivez à ml.agence04@gmail.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Aperçu gratuit de votre site" description="Recevez une maquette personnalisée de votre futur site en 3 à 5 jours. Gratuit, sans carte bancaire et sans engagement." />
      <Navigation />
      <main data-testid="mockup-page" className="relative z-10 min-h-screen px-5 sm:px-8 lg:px-12 pt-36 sm:pt-44 pb-28">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center py-16"
            data-testid="mockup-confirmation"
          >
            <div className="w-20 h-20 rounded-full bg-[var(--accent)] text-black flex items-center justify-center mx-auto mb-10">
              <Check className="w-10 h-10" strokeWidth={2.5} />
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-6xl tracking-tighter leading-[0.9] mb-6">
              Merci !
            </h1>
            <p className="text-zinc-400 text-lg leading-relaxed mb-10">
              On analyse votre univers et on vous prépare votre aperçu dans les
              <span className="text-white"> 3 à 5 prochains jours</span>. On vous
              contacte directement par Instagram ou par message.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/portfolio" className="btn-pill btn-pill-accent">
                Voir le portfolio en attendant
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/" className="btn-pill">Retour à l'accueil</Link>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* LEFT */}
            <div className="lg:col-span-5">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block">
                Aperçu gratuit
              </span>
              <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.88] tracking-tighter mb-8">
                Votre commerce, <span className="text-outline">transformé</span> en 3 à 5 jours.
              </h1>
              <p className="text-zinc-400 text-lg leading-relaxed mb-12">
                Dites-nous quelques mots sur votre commerce. ML conçoit une première
                maquette personnalisée — offerte, sans engagement — et vous l'envoie
                sous 3 à 5 jours.
              </p>

              <ul className="space-y-6">
                {bullets.map((b) => {
                  const Icon = b.icon;
                  return (
                    <li key={b.title} className="flex items-start gap-4">
                      <span className="shrink-0 w-10 h-10 rounded-full border border-white/[0.1] bg-[var(--bg-soft)] flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
                      </span>
                      <div>
                        <div className="font-display text-lg tracking-tight">{b.title}</div>
                        <p className="text-zinc-400 text-sm leading-relaxed mt-1">{b.text}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* RIGHT — form */}
            <div className="lg:col-span-6 lg:col-start-7">
              <form onSubmit={onSubmit} data-testid="mockup-form" className="bento p-7 sm:p-9 space-y-6">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Nom de votre commerce *
                  </label>
                  <input
                    className="field"
                    data-testid="mockup-input-commerce"
                    placeholder="Ex. L'Atelier des Ongles"
                    value={form.commerce}
                    onChange={update("commerce")}
                    required
                  />
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Votre secteur *
                  </label>
                  <select
                    className="field bg-transparent"
                    data-testid="mockup-input-secteur"
                    value={form.secteur}
                    onChange={update("secteur")}
                    required
                  >
                    <option value="" className="bg-black">— Sélectionnez —</option>
                    {secteurOptions.map((s) => (
                      <option key={s.id} value={s.id} className="bg-black">
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Réseaux sociaux ou site actuel
                    <span className="group relative inline-flex">
                      <Info className="w-3.5 h-3.5 text-zinc-500 hover:text-[var(--accent)] cursor-help" />
                      <span className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 w-64 rounded-lg border border-white/10 bg-[#0d0d0d] px-3 py-2 text-[11px] normal-case tracking-normal leading-relaxed text-zinc-300 opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100">
                        Tout ce qui montre votre présence en ligne : site web, Instagram, Facebook, page Google… Indiquez ce que vous avez — et si vous n'avez rien encore, c'est parfait aussi.
                      </span>
                    </span>
                  </label>
                  <input
                    className="field"
                    data-testid="mockup-input-reseau"
                    placeholder="Site web, @instagram, page Facebook…"
                    value={form.reseau}
                    onChange={update("reseau")}
                  />
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Ce que vous aimeriez changer
                  </label>
                  <textarea
                    className="field"
                    data-testid="mockup-input-envie"
                    rows={3}
                    placeholder="Ce qui ne vous plaît pas aujourd'hui, ce que vous rêveriez d'avoir…"
                    value={form.envie}
                    onChange={update("envie")}
                  />
                </div>

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Email
                  </label>
                  <input
                    type="email"
                    className="field"
                    data-testid="mockup-input-email"
                    placeholder="vous@example.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                  <p className="text-zinc-500 text-xs mt-2">
                    Optionnel — pour recevoir votre aperçu si on ne se croise pas en DM.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="mockup-submit"
                  className="btn-pill btn-pill-accent w-full justify-center disabled:opacity-60"
                >
                  {submitting ? "Envoi en cours…" : "Voir ce qu'on ferait pour mon commerce"}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Aucune carte bancaire • Aucun engagement
                </p>
              </form>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
